import React, { useState, useEffect, useContext } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Box } from '@material-ui/core';
import { useRouteMatch } from 'react-router';
import { useHistory } from 'react-router-dom';

import { ProjectDrawer } from 'src/components';
import { ProjectRoutes } from 'src/scenes/Project/ProjectRoutes';
import { useProjectApi } from 'src/services';
import { WebContext } from 'src/scenes/web-context';
import { useFetchStatus } from 'src/hooks';
import { createForm } from 'src/utils/util';


const drawerWidth = 240;
const mainDrawerWidth = 97;

const useStyles = makeStyles(() => ({
  mainContainer: {
    width: `calc(100% - ${mainDrawerWidth + drawerWidth}px)`,
  },
}));

const ProjectContainer = () => {
  const classes = useStyles();
  const { currentUser: { roleId: userRoleId, email: userEmail } } = useContext(WebContext);
  const match = useRouteMatch();
  const projectApi = useProjectApi();
  const history = useHistory();

  const [ interestArea, setInterestArea ] = useState([]);
  const [ isic, setIsic ] = useState();
  const [ project, setProject ] = useState({});

  let { projectId, siteId } = match.params;
  const isSite = Boolean(siteId);
  const currentParentId = projectId;
  projectId = isSite ? siteId : projectId;

  useEffect(() => {
    const fetchData = async () => {
      const { projectList } = await projectApi.getProject(projectId);
      if (!projectList.length) {
        return;
      }
      const [ { name, parentName, id, parentId } ] = projectList;
      if (isSite && parentId !== parseInt(currentParentId, 10)) {
        history.push('/');
        return;
      }
      setProject({
        currentName: name,
        siteName: siteId ? name : undefined,
        siteId: siteId ? id : undefined,
        name: siteId ? parentName : name,
        id: siteId ? parentId : id,
      });
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ projectId ]);

  const { handleConfirm: fetchInterestArea, ...interestAreaFetchStatus } = useFetchStatus({
    confirmFn: async () => {
      const interestArea = await projectApi.getInterestArea({ projectId });
      setInterestArea(interestArea.layers);
    },
  });

  const addInterestArea = handleLeafletLegend => async newInterestArea => {
    const { kmlData } = newInterestArea;
    const form = createForm(newInterestArea.config);
    form.append('kmlFile', kmlData.kmlFile, kmlData.fileName);
    const { areaOfInterestKmlId } = await projectApi.importInterestArea({ projectId, interestArea: form });

    const newLayer = {
      geojson: newInterestArea.layer.geojson.features,
      id: areaOfInterestKmlId,
      name: kmlData.fileName,
    };

    setInterestArea(prevLayers => {
      const layers = [ newLayer, ...prevLayers ];
      handleLeafletLegend(layers);
      return layers;
    });
  };

  const deleteInterestArea = handleLeafletLegend => async areaOfInterestKmlId => {
    await projectApi.deleteInterestArea({ projectId, areaOfInterestKmlId });
    setInterestArea(prevLayers => {
      const layers = prevLayers.filter(layer => layer.id !== areaOfInterestKmlId);
      handleLeafletLegend(layers);
      return layers;
    });

  };

  const { handleConfirm: fetchIsic } = useFetchStatus({
    confirmFn: async () => {
      const isic = await projectApi.getIsic(projectId);
      setIsic(isic);
    },
  });

  useEffect(() => {
    fetchInterestArea();
    fetchIsic();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    document.title = project.currentName ? `${project.currentName} - Nviro Monitor` : 'Nviro Monitor';
  }, [ project ]);
  return (
    <>
      <ProjectDrawer
        name={project.name}
        userRoleId={userRoleId}
        userEmail={userEmail}
        hasInterestAreas={interestArea?.length > 0}
        hasIsic={Boolean(isic?.id)}
        siteName={project.siteName}
      />
      <Box className={classes.mainContainer}>
        <ProjectRoutes
          project={project}
          interestArea={{ fetchStatus: interestAreaFetchStatus, data: interestArea, actions: { addInterestArea, deleteInterestArea } }}
          userRoleId={userRoleId}
        />
      </Box>
    </>
  );
};


export { ProjectContainer };
